import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, Container, Input, Button, Label, Row, Col, Form, FormGroup } from 'reactstrap';
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import { getActiveUserListAPIURL, getUserWiseDurationReportAPIURL } from 'BaseComponentAPI/UserWiseDurationReportAPI';
import { getUserListForCallAvailabilityAPIURL } from 'BaseComponentAPI/CallAvailability';
import axios from 'axios';
import { useFormik } from 'formik';
import TableContainer from 'components/Common/TableContainer';
import * as Yup from 'yup';
import Select from 'react-select';
import moment from 'moment';
import Spinners from 'components/Common/Spinner';
import PopupWindow from 'components/Common/PopupWindow';
import Loader from 'components/Common/Loader';
import * as XLSX from 'xlsx';  // Import the xlsx library
import { checkPermissions } from 'components/Common/ChkPermissions';
import CantAccessPage from 'components/Common/CantAccessPage';


const UserWiseDurationReport = () => {

    const authUser = useSelector(state => state.Login);
    document.title = "Cockpit";

    const storedPermissions = JSON.parse(localStorage.getItem('permissions')) || {};
    const { canRead, canCreate, canChange } = checkPermissions(storedPermissions);

    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');  // Set the message from passed state
    const [popupType, setPopupType] = useState('');  // Set the type from passed state

    const openPopup = (message, type) => {
        setPopupMessage(message);
        setPopupType(type);
        setIsPopupVisible(true);
    };

    const closePopup = () => {
        setIsPopupVisible(false);
    };

    const columns = useMemo(
        () => [
            {
                header: "Activity Date & Time",
                accessorKey: "activityDateTime",
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: "User Activity",
                accessorKey: "userActivity",
                enableColumnFilter: false,
                enableSorting: true,
                cell: ({ cell }) => {
                    const userActivity = cell.getValue();
                    switch (userActivity) {
                        case 1:
                            return "Outgoing";
                        case 2:
                            return "Incomming";
                        case 3:
                            return "Missed Call";
                        default:
                            return "-";
                    }
                },
            },
            {
                header: "Mobile Number",
                accessorKey: "mobileNo",
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: "Talktime Duration",
                accessorKey: "talktimeDuration",
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: "Call Type",
                accessorKey: "callType",
                enableColumnFilter: false,
                enableSorting: true,
                cell: ({ cell }) => {
                    const callType = cell.getValue();
                    if (callType < 1) {
                        return "-"; // Show dash for callType < 1
                    }
                    switch (callType) {
                        case 1:
                            return "Live";
                        case 2:
                            return "System";
                        case 3:
                            return "Cold";
                        default:
                            return "All";
                    }
                },
            },
            {
                header: "Is Customer",
                accessorKey: "isLeadOrCustomer",
                enableColumnFilter: false,
                enableSorting: true,
                cell: ({ cell }) => (cell.getValue() === 1 ? "Yes" : "No"),
            },
            {
                header: "Is Open Enquiry",
                accessorKey: "isOpenEnquiry",
                enableColumnFilter: false,
                enableSorting: true,
                cell: ({ cell }) => {
                    const isOpenEnquiry = cell.getValue();
                    return isOpenEnquiry < 0 ? "-" : isOpenEnquiry === 1 ? "Yes" : "No"; // Show dash for isOpenEnquiry < 0
                },
            },
            {
                header: "Is Included In Call Duration",
                accessorKey: "isIncludedAtDuration",
                enableColumnFilter: false,
                enableSorting: true,
                cell: ({ cell }) => (cell.getValue() === 1 ? "Yes" : "No"),
            },
            {
                header: "Break Duration",
                accessorKey: "breakTime",
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: "Disposition",
                accessorKey: "callDisposition",
                enableColumnFilter: false,
                enableSorting: true,
            },
        ],
        []
    );



    const [data, setData] = useState([]);
    const [submittedCallType, setSubmittedCallType] = useState("");
    const [pageNo, setPageNo] = useState(1);  // Track the current page number
    const [pageSize, setPageSize] = useState(10);  // Track page size
    const [totalPages, setTotalPages] = useState('');
    const [totalItems, setTotalItems] = useState('');
    const [nextPage, setNextPage] = useState('');
    const [previousPage, setPreviousPage] = useState('');
    const [currentPage, setCurrentPage] = useState('');
    const customePageSize = [10, 20, 30, 40, 50]

    const [employeeData, setEmployeeData] = useState([]);
    const [employeeId, setEmployeeId] = useState('');
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [loaderOption, setLoaderOption] = useState(false);
    const [disabelSaveBtn, setDisableSaveBtn] = useState(false);

    // Convert employees array to the format that react-select needs
    const employeeOptions = employeeData.map(emp => ({
        value: emp.userUUID,
        label: emp.userName
    }))

    // Handle the change for the employee ID
    const handleEmployeeIdChange = (e) => {
        setEmployeeId(e.target.value);
    }

    // Handle date change (for both fromDate and toDate)
    const handleDateChange = (date, field) => {
        setData([]);
        formik.setFieldValue(field, date);
    };

    useEffect(() => {
        getData();
    }, [authUser]);


    const getData = async () => {
        setLoaderOption(true);
        if (authUser.headers && Object.keys(authUser.headers).length > 0) {


            try {
                const response = await axios.get(
                    `${getActiveUserListAPIURL}?userUUID=${authUser.headers.userUUID}&isPagination=0&pageNo=0&perPageRec=0`,
                    {
                        headers: { ...authUser.headers },
                        'Authorization': `Bearer ${authUser.headers.access_token}`
                    }
                );

                if (response.data.responseMsg === "Success") {
                    setEmployeeData(response.data.data.items);
                    setTotalPages(response.data.data.totalPages)
                    setTotalItems(response.data.data.totalItems);
                    setNextPage(response.data.data.nextPage);
                    setPreviousPage(response.data.data.previousPage);
                    setCurrentPage(response.data.data.currentPage);
                    setLoaderOption(false);
                }
                else {
                    console.error('API Response Error:', response.data.responseMsg);
                    setLoaderOption(false);
                    openPopup('Something went wrong!', 'error')
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoaderOption(false);
                openPopup('Something went wrong!', 'error')
            }
            // finally {
            //     setLoaderOption(false);
            // }
        }
    };

    const handlePageChange = async (newPageNo, newPageSize) => {
        if (formik.values.toDate != '' || formik.values.fromDate != '' || employeeId != '') {
            if (authUser.headers && Object.keys(authUser.headers).length > 0) {
                // console.log(`API URL: ${getUserWiseDurationReportAPIURL}?employeeUUID=${values.employeeId}&startDate=${moment(values.fromDate).format("DD/MM/YYYY")}&endDate=${moment(values.toDate).format("DD/MM/YYYY")}&pageNo=${pageNo}&perPageRec=${pageSize}`);

                try {
                    const response = await axios.get(
                        `${getUserWiseDurationReportAPIURL}?employeeUUID=${formik.values.employeeId}&startDate=${moment(formik.values.fromDate).format("DD/MM/YYYY")}&endDate=${moment(formik.values.toDate).format("DD/MM/YYYY")}&isPagination=1&pageNo=${newPageNo}&perPageRec=${newPageSize}`,
                        {
                            headers: { ...authUser.headers },
                            'Authorization': `Bearer ${authUser.headers.access_token}`
                        }
                    );

                    if (response.data.responseMsg === "Records not found") {
                        setLoaderOption(false);
                        setDisableSaveBtn(false);
                        openPopup('Records Not Found.', 'error')
                    }
                    else if (response.data.responseMsg === "Success") {
                        setData(response.data.data.items);
                        setTotalPages(response.data.data.totalPages)
                        setTotalItems(response.data.data.totalItems);
                        setNextPage(response.data.data.nextPage);
                        setPreviousPage(response.data.data.previousPage);
                        setCurrentPage(response.data.data.currentPage);
                        setLoaderOption(false);
                        setDisableSaveBtn(false);
                    }
                    else {
                        console.error('API Response Error:', response.data.responseMsg);
                        setLoaderOption(false);
                        setDisableSaveBtn(false);
                        openPopup(response.data.responseMsg, 'error')
                    }
                } catch (error) {
                    console.error('Error fetching data:', error);
                    setLoaderOption(false);
                    setDisableSaveBtn(false);
                    openPopup("Requested Data Not Found / 404", 'warn')
                }
                //  finally {
                //     setLoaderOption(false);
                // }
            }
        }
    };

    const formik = useFormik({
        initialValues: {
            employeeId: '',  // Employee selection field
            fromDate: null,
            toDate: null
        },
        validationSchema: Yup.object({
            employeeId: Yup.string()
                .required('Employee is required'),  // Employee ID is required
            fromDate: Yup.date()
                .required('From Date is required'),
            toDate: Yup.date()
                .required('To Date is required')
                .test('max-date-range', 'To Date must be within 3 days of From Date', function (value) {
                    const { fromDate } = this.parent; // Access fromDate from the form values
                    if (fromDate && value) {
                        const maxDate = moment(fromDate).add(3, 'days'); // Add 3 days to fromDate
                        return moment(value).isSameOrBefore(maxDate, 'day'); // Check if toDate is within range
                    }
                    return true; // If fromDate or toDate is not set, skip the test
                }),
        }),

        onSubmit: async (values) => {
            setLoaderOption(true);
            setDisableSaveBtn(true);
            setData([]);
            setTotalPages('');
            setTotalItems('');
            setNextPage('');
            setPreviousPage('');
            setCurrentPage('');
            // console.log(values)
            if (authUser.headers && Object.keys(authUser.headers).length > 0) {
                // console.log(`API URL: ${getUserWiseDurationReportAPIURL}?employeeUUID=${values.employeeId}&startDate=${moment(values.fromDate).format("DD/MM/YYYY")}&endDate=${moment(values.toDate).format("DD/MM/YYYY")}&pageNo=${pageNo}&perPageRec=${pageSize}`);

                try {
                    const response = await axios.get(
                        `${getUserWiseDurationReportAPIURL}?employeeUUID=${values.employeeId}&startDate=${moment(values.fromDate).format("DD/MM/YYYY")}&endDate=${moment(values.toDate).format("DD/MM/YYYY")}&isPagination=1&pageNo=${pageNo}&perPageRec=${pageSize}`,
                        {
                            headers: { ...authUser.headers },
                            'Authorization': `Bearer ${authUser.headers.access_token}`
                        }
                    );

                    if (response.data.responseMsg === "Records not found") {
                        setLoaderOption(false);
                        setDisableSaveBtn(false);
                        openPopup('Records Not Found.', 'error')
                    }
                    else if (response.data.responseMsg === "Success") {
                        setData(response.data.data.items);
                        setTotalPages(response.data.data.totalPages)
                        setTotalItems(response.data.data.totalItems);
                        setNextPage(response.data.data.nextPage);
                        setPreviousPage(response.data.data.previousPage);
                        setCurrentPage(response.data.data.currentPage);
                        setLoaderOption(false);
                        setDisableSaveBtn(false);
                    }
                    else {
                        console.error('API Response Error:', response.data.responseMsg);
                        setLoaderOption(false);
                        setDisableSaveBtn(false);
                        openPopup(response.data.responseMsg, 'error')
                    }
                } catch (error) {
                    console.error('Error fetching data:', error);
                    setLoaderOption(false);
                    setDisableSaveBtn(false);
                    openPopup("Requested Data Not Found / 404", 'warn')
                }
                //  finally {
                //     setLoaderOption(false);
                // }
            }

        },
    });


    const handleEmployeeChange = (selectedOption) => {
        setData([]);
        formik.setFieldValue("fromDate", '');
        formik.setFieldValue("toDate", '');
        const selectedEmployeeId = selectedOption ? selectedOption.value : '';
        formik.setFieldValue('employeeId', selectedEmployeeId);  // Set employeeId in formik state
    };

    const exportToExcel = async () => {
        setLoaderOption(true);
        if (authUser.headers && Object.keys(authUser.headers).length > 0 && data.length !== 0) {
            try {
                const response = await axios.get(
                    `${getUserWiseDurationReportAPIURL}?employeeUUID=${formik.values.employeeId}&startDate=${moment(formik.values.fromDate).format("DD/MM/YYYY")}&endDate=${moment(formik.values.toDate).format("DD/MM/YYYY")}&isPagination=0&pageNo=0&perPageRec=0`,
                    {
                        headers: { ...authUser.headers },
                        'Authorization': `Bearer ${authUser.headers.access_token}`
                    }
                );

                if (response.data.responseMsg === "Records not found") {
                    setLoaderOption(false);
                    openPopup("Records Not Found.", "error");
                } else if (response.data.responseMsg === "Success") {
                    const rawData = response.data.data.items; // API response data

                    // Exclude "Action" column
                    const filteredColumns = columns.filter(col => col.header !== "Action");

                    // Define headers for Excel
                    const columnHeaders = filteredColumns.map(col => col.header);

                    // Apply the same transformations for exporting
                    const formattedData = rawData.map(item =>
                        filteredColumns.map(col => {
                            const value = item[col.accessorKey] ?? "";

                            // Apply column-specific formatting
                            switch (col.accessorKey) {
                                case "userActivity":
                                    return value === 1 ? "Outgoing" :
                                        value === 2 ? "Incoming" :
                                            value === 3 ? "Missed Call" : "-";
                                case "callType":
                                    return value < 1 ? "-" :
                                        value === 1 ? "Live" :
                                            value === 2 ? "System" :
                                                value === 3 ? "Cold" : "All";
                                case "isLeadOrCustomer":
                                case "isIncludedAtDuration":
                                    return value === 1 ? "Yes" : "No";
                                case "isOpenEnquiry":
                                    return value < 0 ? "-" : value === 1 ? "Yes" : "No";
                                default:
                                    return value;
                            }
                        })
                    );

                    // Combine headers and data
                    const sheetData = [columnHeaders, ...formattedData];

                    // Create worksheet and workbook
                    const ws = XLSX.utils.aoa_to_sheet(sheetData);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, "User Wise Duration Report");

                    // Generate timestamp for filename
                    const currentDate = new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
                    const formattedDate = currentDate.replace(",", "").replace(/\//g, "-").replace(/:/g, "-").split(" ")[0] + " " + currentDate.split(" ")[1];

                    const empName = employeeOptions.find(option => option.value === formik.values.employeeId);
                    const fileName = `${empName.label} ${formattedDate}.xlsx`;

                    // Export the Excel file
                    XLSX.writeFile(wb, fileName);

                    setLoaderOption(false);
                } else {
                    console.error("API Response Error:", response.data.responseMsg);
                    setLoaderOption(false);
                    openPopup(response.data.responseMsg, "error");
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                setLoaderOption(false);
                openPopup(error.message, "error");
            }
        }
    };


    if (!canRead) {
        return (
            <>
                <React.Fragment>
                    <div className="page-content">
                        <Container fluid>
                            <Breadcrumbs title="Report" breadcrumbItem="Upload Cold Calls" />
                            <CantAccessPage />
                        </Container>
                    </div>
                </React.Fragment>
            </>
        )
    }
    else {
        if (loaderOption == true) {
            return (
                <>
                    <Loader />
                </>
            )
        }
        else {
            return (
                <React.Fragment>
                    <div className="page-content">
                        <Container fluid>
                            <Breadcrumbs title="Report" breadcrumbItem="User wise Duration Report" />
                            <Card>
                                <CardBody className="border-bottom">
                                    <div style={{ padding: '20px' }}>

                                        <Form onSubmit={formik.handleSubmit}>
                                            <Row>
                                                {/* Employee Select */}
                                                <Col md="4">
                                                    <FormGroup>
                                                        <Label for="employeeId">Employee</Label>
                                                        <Select
                                                            id="employeeId"
                                                            name="employeeId"
                                                            className="form-control-md"
                                                            options={employeeOptions}
                                                            value={employeeOptions.find(option => option.value === formik.values.employeeId)}
                                                            onChange={handleEmployeeChange}
                                                            onBlur={formik.handleBlur}
                                                            placeholder="Select Employee"
                                                        />
                                                        {formik.touched.employeeId && formik.errors.employeeId && (
                                                            <div style={{ color: 'red', marginTop: '5px' }}>
                                                                {formik.errors.employeeId}
                                                            </div>
                                                        )}
                                                    </FormGroup>
                                                </Col>

                                            </Row>

                                            <Row className='my-3'>
                                                {/* From Date Picker */}
                                                <Col md="4">
                                                    <FormGroup>
                                                        <Label for="fromDate">From Date</Label>
                                                        <DatePicker
                                                            selected={formik.values.fromDate}
                                                            onChange={(date) => { handleDateChange(date, 'fromDate'); formik.setFieldValue("toDate", ''); }}
                                                            dateFormat="dd/MM/yyyy"
                                                            className="form-control"
                                                            placeholderText="Select From Date"
                                                            popperPlacement="bottom"
                                                            portalId="root"
                                                            renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        justifyContent: "space-between",
                                                                        alignItems: "center",
                                                                        padding: "8px 12px",
                                                                        borderBottom: "1px solid #eee",
                                                                        background: "#fff",
                                                                    }}
                                                                >
                                                                    <button
                                                                        onClick={decreaseMonth}
                                                                        type="button"
                                                                        style={{
                                                                            border: "none",
                                                                            background: "transparent",
                                                                            fontSize: "18px",
                                                                            cursor: "pointer",
                                                                        }}
                                                                    >
                                                                        ◀
                                                                    </button>

                                                                    <span style={{ fontSize: "16px", fontWeight: "600" }}>
                                                                        {date.toLocaleDateString("en-US", {
                                                                            month: "long",
                                                                            year: "numeric",
                                                                        })}
                                                                    </span>

                                                                    <button
                                                                        onClick={increaseMonth}
                                                                        type="button"
                                                                        style={{
                                                                            border: "none",
                                                                            background: "transparent",
                                                                            fontSize: "18px",
                                                                            cursor: "pointer",
                                                                        }}
                                                                    >
                                                                        ▶
                                                                    </button>
                                                                </div>
                                                            )}
                                                        />
                                                        {formik.touched.fromDate && formik.errors.fromDate && (
                                                            <div style={{ color: 'red', marginTop: '5px' }}>
                                                                {formik.errors.fromDate}
                                                            </div>
                                                        )}
                                                    </FormGroup>
                                                </Col>

                                                {/* To Date Picker */}
                                                <Col md="4">
                                                    <FormGroup>
                                                        <Label for="toDate">To Date</Label>
                                                        <DatePicker
                                                            selected={formik.values.toDate}
                                                            onChange={(date) => handleDateChange(date, 'toDate')}
                                                            dateFormat="dd/MM/yyyy"
                                                            className="form-control"
                                                            placeholderText="Select To Date"
                                                            popperPlacement="bottom"
                                                            portalId="root"
                                                            renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        justifyContent: "space-between",
                                                                        alignItems: "center",
                                                                        padding: "8px 12px",
                                                                        borderBottom: "1px solid #eee",
                                                                        background: "#fff",
                                                                    }}
                                                                >
                                                                    <button
                                                                        onClick={decreaseMonth}
                                                                        type="button"
                                                                        style={{
                                                                            border: "none",
                                                                            background: "transparent",
                                                                            fontSize: "18px",
                                                                            cursor: "pointer",
                                                                        }}
                                                                    >
                                                                        ◀
                                                                    </button>

                                                                    <span style={{ fontSize: "16px", fontWeight: "600" }}>
                                                                        {date.toLocaleDateString("en-US", {
                                                                            month: "long",
                                                                            year: "numeric",
                                                                        })}
                                                                    </span>

                                                                    <button
                                                                        onClick={increaseMonth}
                                                                        type="button"
                                                                        style={{
                                                                            border: "none",
                                                                            background: "transparent",
                                                                            fontSize: "18px",
                                                                            cursor: "pointer",
                                                                        }}
                                                                    >
                                                                        ▶
                                                                    </button>
                                                                </div>
                                                            )}
                                                        />
                                                        {formik.touched.toDate && formik.errors.toDate && (
                                                            <div style={{ color: 'red', marginTop: '5px' }}>
                                                                {formik.errors.toDate}
                                                            </div>
                                                        )}
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            <Row className="mt-3">
                                                {/* Submit Button */}
                                                <Col md="12">
                                                    <Button color="primary" type="submit" disabled={disabelSaveBtn || !canRead}>
                                                        Submit
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </Form>
                                    </div>

                                    {
                                        data.length != 0
                                            ?
                                            <div className='mt-3'>
                                                <div className="d-flex align-items-center justify-content-end mb-2">
                                                    <div className="flex-shrink-0">
                                                        <button
                                                            onClick={exportToExcel}  // Trigger the export function
                                                            className="btn btn-primary me-1"
                                                        >
                                                            Export Excel
                                                        </button>
                                                    </div>
                                                </div>
                                                <TableContainer
                                                    columns={columns}
                                                    data={data || []}
                                                    isGlobalFilter={true}
                                                    isPagination={true}
                                                    SearchPlaceholder="Search..."
                                                    isCustomPageSize={true}
                                                    tableClass="align-middle table-nowrap table-hover dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                                    theadClass="table-light"
                                                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                                                    pagination="pagination"
                                                    getRowProps={row => ({
                                                        className: "",
                                                    })}
                                                    onPageChange={handlePageChange} // Pass page change handler
                                                    onPageSizeChange={handlePageChange} // Pass page size change handler
                                                    pageSize={pageSize}
                                                    setPageSize={setPageSize}
                                                    totalPages={totalPages}
                                                    totalItems={totalItems}
                                                    tableNextPage={nextPage}
                                                    tablePreviousPage={previousPage}
                                                    tableCurrentPage={currentPage}
                                                    customePageSize={customePageSize}
                                                />
                                            </div>
                                            :
                                            ''
                                    }
                                </CardBody>
                            </Card>
                        </Container>
                    </div>

                    {isPopupVisible && (
                        <PopupWindow message={popupMessage} type={popupType} onClose={closePopup} />
                    )}
                </React.Fragment>
            );
        }
    }
}

UserWiseDurationReport.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(UserWiseDurationReport);
